import { PlusCircleOutlined } from '@ant-design/icons'
import { Button, message } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StyledModal } from '../styledComponents'
import InputForm from './InputForm'
import INTERNAL_PROPERTIES from '../UploadExcelButton/internalProperties'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { postPart, putPartsToGroup } from '$api/client'
import { theme } from 'antd'
import { isDemoError } from '$utils/demo'
import { createClientMaterial, fetchClientMaterialById } from '$api/client'

const { useToken } = theme

const CreatePartButton = ({ groups, activeGroup }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(false)
  const { token } = useToken()

  const addPartMutation = useMutation({
    mutationFn: postPart,
    onSuccess: (part, variables) => {
      if (variables.group) {
        addToGroupMutation.mutate({
          partsIds: [part.data.id],
          groupId: variables.group,
        })
      } else {
        setLoading(false)
        queryClient.refetchQueries(['allpartspaginated'])
        queryClient.invalidateQueries(['parts'])
      }
      queryClient.invalidateQueries({ queryKey: ['clientMaterialsList'] })
    },
    onError: (err) => {
      setLoading(false)
      console.error(err)
      if (!isDemoError(err)) {
        message.error(`there was an error!`)
      }
    },
  })

  const addToGroupMutation = useMutation({
    mutationFn: putPartsToGroup,
    onSuccess: () => {
      setLoading(false)
      queryClient.refetchQueries(['allpartspaginated'])
      queryClient.invalidateQueries(['parts'])
    },
    onError: (err) => {
      setLoading(false)
      console.error(err)
      if (!isDemoError(err)) {
        message.error(`there was an error!`)
      }
    },
  })

  const createMaterialMutation = useMutation({
    mutationFn: createClientMaterial,
    onSuccess: (_data) => {
      queryClient.invalidateQueries({
        queryKey: ['clientMaterialsListForDropdown'],
      })
    },
  })

  const getPart = async (mapping) => {
    let clientMaterialId = mapping['Internal Material']

    let selectedClientMaterial = null

    if (clientMaterialId && !clientMaterialId.match(/^[0-9a-fA-F]{24}$/)) {
      try {
        const newMaterial = await createMaterialMutation.mutateAsync({
          name: clientMaterialId,
        })
        clientMaterialId = newMaterial.id
        selectedClientMaterial = newMaterial
      } catch (error) {
        message.error(t('error_creating_material'))
        console.error('Failed to create new client material:', error)
        return null
      }
    } else if (clientMaterialId) {
      try {
        selectedClientMaterial = await queryClient.fetchQuery({
          queryKey: ['clientMaterial', clientMaterialId],
          queryFn: () => fetchClientMaterialById(clientMaterialId),
        })
      } catch (error) {
        message.error(t('error_fetching_material_details'))
        console.error('Failed to fetch client material details:', error)
        return null
      }
    }

    const partToAdd = {
      id_part_client: mapping.id_part_client,
      name: mapping.name,
      attributes: [],
      float_attributes: [],
      status_attributes: [],
      client_material_id: clientMaterialId || null,
      dwh_materials: selectedClientMaterial?.mapped_dwh_material_ids || [],
    }
    partToAdd.client_material_id = clientMaterialId

    if (selectedClientMaterial) {
      partToAdd.attributes.push({
        key: 'Material',
        value: selectedClientMaterial.name,
        description: null,
      })
    }

    for (const internalProperty of INTERNAL_PROPERTIES.filter(
      (val) => val.float_attribute,
    )) {
      const value = mapping[internalProperty.name]
      if (typeof value === 'number') {
        partToAdd.float_attributes.push({
          key: internalProperty.name,
          value: value * (mapping?.[`${internalProperty.name}-unit`] || 1),
        })
      }
    }

    // add other attributes
    for (const internalProperty of INTERNAL_PROPERTIES.filter(
      (val) => val.attribute && val.name !== 'Material',
    )) {
      const value = mapping[internalProperty.name]
      if (value) {
        partToAdd.attributes.push({
          key: internalProperty.name,
          value: value,
        })
      }
    }

    return partToAdd
  }

  const handleOk = async (mappingFormValues) => {
    setIsModalOpen(false)

    const partData = await getPart(mappingFormValues)
    if (!partData) {
      return
    }
    if (typeof mappingFormValues.group === 'object') {
      mappingFormValues.group = mappingFormValues.group.value
    }

    addPartMutation.mutate(
      { ...partData, group: mappingFormValues.group },
      {
        onSuccess: () => {
          message.success(
            t('parts_master_data.create.success_message', {
              count: 1,
            }),
          )
        },
      },
    )
  }

  const handleCancel = () => {
    setIsModalOpen(false)
  }

  return (
    <>
      <Button
        onClick={() => setIsModalOpen(true)}
        icon={<PlusCircleOutlined />}
        size="large"
        loading={loading}
        style={{
          borderRadius: '0 10px 10px 0',
          borderColor: token.colorPrimary,
          color: token.colorPrimary.substring(1),
        }}
      >
        {t('parts_master_data.create.new_button')}
      </Button>
      <StyledModal
        destroyOnClose="true"
        centered="true"
        title={t('parts_master_data.create.create_part')}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        maskClosable={false}
        footer={[
          <Button
            form="addPartForm"
            key="submit"
            htmlType="submit"
            type="primary"
          >
            {t('button.create')}
          </Button>,
        ]}
      >
        <InputForm
          name="addPartForm"
          internalProperties={INTERNAL_PROPERTIES}
          handleOk={handleOk}
          groups={groups}
          activeGroup={activeGroup}
        />
      </StyledModal>
    </>
  )
}
export default CreatePartButton
